import React, { useRef, useImperativeHandle, forwardRef, useEffect } from "react";
import EmailEditor from "react-email-editor";

export interface VisualEditorRef {
  insertVariable: (variable: string) => void;
  reset: () => void;
  exportHtml: () => Promise<{ html: string; design?: any }>;
  saveDesign: () => Promise<any>;
}

interface VisualEditorProps {
  html?: string;
  onHtmlChange?: (html: string) => void;
}

const VisualEditor = forwardRef<VisualEditorRef, VisualEditorProps>(
  ({ html = "", onHtmlChange }, ref) => {
    const emailEditorRef = useRef<any>(null);

    useImperativeHandle(ref, () => ({
      insertVariable: (variable: string) => {
        try {
          const editor = emailEditorRef.current?.editor;
          // try common helper methods if available
          if (editor && typeof editor.insertHtml === "function") {
            editor.insertHtml(variable);
            // trigger export to update parent
            editor.exportHtml((data: any) => onHtmlChange?.(data.html || ""));
            return;
          }

          // fallback: export current html, append variable, and load as html if import available
          if (editor && typeof editor.exportHtml === "function") {
            editor.exportHtml((data: any) => {
              const newHtml = (data.html || "") + variable;
              if (typeof editor.loadDesign === "function") {
                // try to import HTML if API supports it
                if (typeof (emailEditorRef.current as any).editor?.loadDesign === "function") {
                  // best-effort: loadDesign expects a design JSON; skip if not supported
                }
              }
              onHtmlChange?.(newHtml);
            });
            return;
          }

          console.warn("VisualEditor: unable to insert variable into editor instance");
        } catch (err) {
          console.error("VisualEditor insertVariable error", err);
        }
      },
      reset: () => {
        try {
          const editor = emailEditorRef.current?.editor;
          if (editor && typeof editor.loadDesign === "function") {
            // load an empty design
            editor.loadDesign({ body: [] });
          }
        } catch (err) {
          console.error("VisualEditor reset error", err);
        }
      },
      exportHtml: () => {
        return new Promise((resolve, reject) => {
          try {
            const editor = emailEditorRef.current?.editor;
            if (editor && typeof editor.exportHtml === "function") {
              editor.exportHtml((data: any) => {
                resolve({ html: data.html || "", design: data.design });
              });
              return;
            }
            reject(new Error("exportHtml not available on editor instance"));
          } catch (err) {
            reject(err);
          }
        });
      },
      saveDesign: () => {
        return new Promise((resolve, reject) => {
          try {
            const editor = emailEditorRef.current?.editor;
            if (editor && typeof editor.saveDesign === "function") {
              editor.saveDesign((design: any) => resolve(design));
              return;
            }
            // fallback: try exportHtml and return design from it
            if (editor && typeof editor.exportHtml === "function") {
              editor.exportHtml((data: any) => resolve(data.design));
              return;
            }
            reject(new Error("saveDesign not available on editor instance"));
          } catch (err) {
            reject(err);
          }
        });
      },
    }));

    useEffect(() => {
      // if initial HTML is provided, try to import into the editor when it becomes available
      const editor = emailEditorRef.current?.editor;
      if (!html || !editor) return;
      try {
        if (typeof editor.loadDesign === "function" && typeof (emailEditorRef.current as any).editor?.disable !== "function") {
          // no reliable importHtml API across versions; we just notify parent instead
        }
      } catch (err) {
        // ignore
      }
    }, [html]);

    const onLoad = () => {
      // export initial html to parent so ComposeTab has the current HTML
      try {
        const editor = emailEditorRef.current?.editor;
        if (editor && typeof editor.exportHtml === "function") {
          editor.exportHtml((data: any) => {
            onHtmlChange?.(data.html || "");
          });
        }
      } catch (err) {
        console.error("VisualEditor onLoad exportHtml error", err);
      }
    };

    return (
      <div style={{ minHeight: 350, border: "1px solid #e5e7eb", borderRadius: 6, overflow: "hidden" }}>
        <EmailEditor
          ref={emailEditorRef}
          onLoad={onLoad}
        />
      </div>
    );
  }
);

VisualEditor.displayName = "VisualEditor";

export default VisualEditor;
